import {
  List,
  ListItem,
  ListItemPrefix,
  Accordion,
  AccordionHeader,
  AccordionBody,
} from "@material-tailwind/react";

import {
  ChartBarIcon,
  NewspaperIcon,
  InboxIcon,
  PowerIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  // UserPlusIcon,
  CurrencyDollarIcon,
  // ChartBarSquareIcon,
} from "@heroicons/react/24/solid";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

export enum Accord {
  developer = "DEVELOPER",
}

export type SidebarProps = {
  isOpen: boolean;
  isMobileViewport: boolean;
  position: string;
  onClose: () => void;
};

const developerPaths = ["/webhook", "/logs"];

const Sidebar = ({
  isOpen,
  isMobileViewport,
  position,
  onClose,
}: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const sidebarRef = useRef<HTMLElement>(null);
  const isDeveloperPath = developerPaths.some((path) => {
    return location.pathname === path;
  });
  const [open, setOpen] = useState<Accord | undefined>(
    isDeveloperPath ? Accord.developer : undefined,
  );

  useEffect(() => {
    if (isDeveloperPath) {
      setOpen(Accord.developer);
    }
  }, [isDeveloperPath]);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) {
      return;
    }

    const shouldDisableInteraction = isMobileViewport && !isOpen;
    if (shouldDisableInteraction) {
      sidebar.setAttribute("inert", "");
      return;
    }

    sidebar.removeAttribute("inert");
  }, [isMobileViewport, isOpen]);

  const logout = async () => {
    window?.localStorage.removeItem("auth");
    await queryClient.invalidateQueries({ queryKey: ["profile"] });
    navigate("/login");
    onClose();
  };

  const handleOpen = (value: Accord) => {
    setOpen(value === open ? undefined : value);
  };

  const navigateTo = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActivePath = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const navigationItemClass = (isActive: boolean) => {
    const activeClasses =
      isActive ?
        "bg-green-50 text-green-800"
      : "text-blue-gray-600 hover:bg-blue-gray-100 hover:text-blue-gray-900";

    return `min-h-11 rounded-xl px-3 text-sm font-medium transition-colors ${activeClasses}`;
  };

  return (
    <>
      <button
        type="button"
        aria-label="Close navigation menu"
        aria-hidden={!isOpen}
        tabIndex={isOpen ? 0 : -1}
        className={`fixed inset-x-0 bottom-0 top-16 z-[80] bg-blue-gray-900/50 backdrop-blur-[1px] transition-opacity duration-200 lg:hidden ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
      />
      <aside
        id="primary-navigation"
        aria-label="Primary navigation"
        aria-hidden={isMobileViewport && !isOpen}
        ref={sidebarRef}
        className={`fixed bottom-0 left-0 top-16 z-[90] w-[min(86vw,20rem)] max-w-full overflow-y-auto border-r border-blue-gray-100 bg-white px-3 py-4 shadow-xl transition-transform duration-200 ease-out lg:w-72 lg:translate-x-0 lg:px-4 lg:shadow-none ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}>
        <List className="min-w-0 gap-1">
          {/* <ListItem
            selected={isActivePath("/")}
            aria-current={isActivePath("/") ? "page" : undefined}
            className={navigationItemClass(isActivePath("/"))}
            onClick={() => {
              navigateTo("/");
            }}
          >
            <ListItemPrefix>
              <PresentationChartBarIcon className="h-5 w-5 text-current" />
            </ListItemPrefix>
            Overview
          </ListItem> */}
          {/* <ListItem
            selected={isActivePath("/member")}
            aria-current={isActivePath("/member") ? "page" : undefined}
            className={navigationItemClass(isActivePath("/member"))}
            onClick={() => {
              navigateTo("/member");
            }}
          >
            <ListItemPrefix>
              <UsersIcon className="h-5 w-5 text-current" />
            </ListItemPrefix>
            Organization
          </ListItem> */}
          {/* <ListItem
            onClick={() => {
              navigate("/member/create");
            }}
          >
            <ListItemPrefix>
              <UserPlusIcon className="h-5 w-5" />
            </ListItemPrefix>
            Create Member
          </ListItem> */}
          <ListItem
            selected={isActivePath("/ticket")}
            aria-current={isActivePath("/ticket") ? "page" : undefined}
            className={navigationItemClass(isActivePath("/ticket"))}
            onClick={() => {
              navigateTo("/ticket");
            }}>
            <ListItemPrefix>
              <NewspaperIcon className="h-5 w-5 text-current" />
            </ListItemPrefix>
            Ticket
          </ListItem>
          <ListItem
            selected={isActivePath("/report")}
            aria-current={isActivePath("/report") ? "page" : undefined}
            className={navigationItemClass(isActivePath("/report"))}
            onClick={() => {
              navigateTo("/report");
            }}>
            <ListItemPrefix>
              <ChartBarIcon className="h-5 w-5 text-current" />
            </ListItemPrefix>
            Report
          </ListItem>
          {/* <ListItem
            onClick={() => {
              navigate("/state");
            }}
          >
            <ListItemPrefix>
              <ChartBarSquareIcon className="h-5 w-5" />
            </ListItemPrefix>
            Sport State
          </ListItem> */}
          <Accordion
            open={open === Accord.developer}
            icon={
              <ChevronDownIcon
                strokeWidth={2.5}
                className={`mx-auto h-4 w-4 text-blue-gray-500 transition-transform ${
                  open === Accord.developer ? "rotate-180" : ""
                }`}
              />
            }>
            <AccordionHeader
              onClick={() => {
                handleOpen(Accord.developer);
              }}
              className="border-b-0 py-0 text-left text-md font-normal">
              <ListItem
                selected={isDeveloperPath}
                className={navigationItemClass(isDeveloperPath)}>
                <ListItemPrefix>
                  <InboxIcon className="h-5 w-5 text-current" />
                </ListItemPrefix>
                For Developer
              </ListItem>
            </AccordionHeader>
            <AccordionBody className="py-1">
              <List className="p-0">
                <ListItem
                  selected={isActivePath("/webhook")}
                  aria-current={isActivePath("/webhook") ? "page" : undefined}
                  className={navigationItemClass(isActivePath("/webhook"))}
                  onClick={() => {
                    navigateTo("/webhook");
                  }}>
                  <ListItemPrefix>
                    <ChevronRightIcon
                      strokeWidth={3}
                      className="h-3 w-5 text-current"
                    />
                  </ListItemPrefix>
                  Webhook
                </ListItem>
                <ListItem
                  selected={isActivePath("/logs")}
                  aria-current={isActivePath("/logs") ? "page" : undefined}
                  className={navigationItemClass(isActivePath("/logs"))}
                  onClick={() => {
                    navigateTo("/logs");
                  }}>
                  <ListItemPrefix>
                    <ChevronRightIcon
                      strokeWidth={3}
                      className="h-3 w-5 text-current"
                    />
                  </ListItemPrefix>
                  Webhook Logs
                </ListItem>
              </List>
            </AccordionBody>
          </Accordion>

          {/* <ListItem>
            <ListItemPrefix>
              <UserCircleIcon className="h-5 w-5" />
            </ListItemPrefix>
            Profile
          </ListItem>
          <ListItem>
            <ListItemPrefix>
              <Cog6ToothIcon className="h-5 w-5" />
            </ListItemPrefix>
            Settings
          </ListItem> */}
          {["COMPANY", "SHARE_HOLDER"].includes(position) && (
            <ListItem
              selected={isActivePath("/currency")}
              aria-current={isActivePath("/currency") ? "page" : undefined}
              className={navigationItemClass(isActivePath("/currency"))}
              onClick={() => {
                navigateTo("/currency");
              }}>
              <ListItemPrefix>
                <CurrencyDollarIcon className="h-5 w-5 text-current" />
              </ListItemPrefix>
              Currency
            </ListItem>
          )}
          <ListItem
            className={navigationItemClass(false)}
            onClick={() => {
              logout();
            }}>
            <ListItemPrefix>
              <PowerIcon className="h-5 w-5 text-current" />
            </ListItemPrefix>
            Log Out
          </ListItem>
        </List>
      </aside>
    </>
  );
};

export default Sidebar;
